/**
 * Grade12Popup - one-shot post-onboarding prompt asking the grade-12
 * cohort question. Used to live as step 3 of the onboarding flow; now
 * surfaces here so onboarding ends at /subjects directly.
 *
 * The question covers two cases at once: currently in 12ª, or finished
 * and wanting to revise with those exams. Both want the same thing, the
 * 12ª content unlocked, so both answer Sim. Before this, only "Sim" set
 * preferred_mode='both', which left anyone revising with no way to reach
 * the content at all.
 *
 * "Outro" was removed. It POSTed is_current_grade_12: null and persisted
 * the dismissal, which is exactly what the ✕ already does, so it was a
 * third button for a path that already had one.
 *
 * Trigger conditions (parent decides; component is presentational):
 *   - student.is_current_grade_12 === null (never answered)
 *   - !localStorage[`grade12_popup_dismissed_${student_id}`]
 *
 * Once dismissed (via either the Sim/Não/Outro buttons OR ✕), the
 * localStorage flag is set permanently for that account so the popup
 * never re-fires. Existing students who answered the question in the
 * old onboarding flow are skipped because is_current_grade_12 isn't
 * null for them.
 *
 * 2026-04-28 launch flow.
 */

import React, { useState } from 'react'
import { api } from '../lib/api'

export interface Grade12PopupProps {
  /** Student id - used for the localStorage dismissal key so two
   *  accounts on the same device don't share a dismissal. */
  studentId: string
  /** Called after a successful answer (or skip). Parent should refetch
   *  the user so `is_current_grade_12` reflects the saved value.
   *  The `chosen` argument lets the parent react to the specific
   *  outcome - most importantly, switching the /subjects view into the
   *  12ª toggle when the answer is 'yes'. */
  onClose: (chosen: 'yes' | 'no' | 'skip') => void
}

const dismissalKey = (id: string) => `grade12_popup_dismissed_${id}`
const pendingGrade12Key = (id: string) => `grade12_opt_in_pending_${id}`

export const Grade12Popup: React.FC<Grade12PopupProps> = ({ studentId, onClose }) => {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const persistDismissal = () => {
    try { localStorage.setItem(dismissalKey(studentId), '1') } catch {}
  }

  const choose = async (value: 'yes' | 'no') => {
    setBusy(true)
    setError(null)
    try {
      const eoy = new Date().getFullYear()
      // 'Sim' also bumps preferred_mode to 'both' so the /subjects
      // mode toggle becomes available - without that, the 12ª view
      // stays hidden behind the global switch and answering Sim
      // visibly does nothing. The page re-renders into the 12ª view
      // after onClose('yes') below (see SubjectSelectPage's onAnswered).
      const payload =
        value === 'yes' ? { is_current_grade_12: true, expected_grade_12_completion: `${eoy}-12-31`, preferred_mode: 'both' as const }
      :                   { is_current_grade_12: false }
      await api.post('/auth/profile-contact', payload)
      if (value === 'yes') {
        try { sessionStorage.setItem(pendingGrade12Key(studentId), '1') } catch {}
      }
      persistDismissal()
      onClose(value)
    } catch (err: any) {
      setError(err?.message ?? 'Erro ao guardar')
    } finally {
      setBusy(false)
    }
  }

  const skipAndDismiss = () => {
    // Skip without writing to the DB - but mark dismissed so we don't
    // pester. is_current_grade_12 stays null; can be answered later
    // via /profile.
    persistDismissal()
    onClose('skip')
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <span className="text-3xl">🎓</span>
          <button
            onClick={skipAndDismiss}
            aria-label="Dispensar"
            className="text-gray-300 hover:text-gray-500 text-2xl leading-none"
          >
            ×
          </button>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Estás na 12.ª classe agora ou gostarias de rever os exames?
        </h3>
        <p className="text-xs text-gray-500 leading-relaxed mb-5">
          Ajuda-nos a personalizar lembretes e estatísticas para a tua etapa.
        </p>
        <div className="grid grid-cols-2 gap-2">
          {[
            { v: 'yes', label: 'Sim' },
            { v: 'no',  label: 'Não' },
          ].map(opt => (
            <button
              key={opt.v}
              onClick={() => choose(opt.v as 'yes' | 'no')}
              disabled={busy}
              className="text-sm font-semibold px-4 py-2.5 rounded-lg border border-gray-200 bg-white hover:border-indigo-400 hover:bg-indigo-50 disabled:opacity-50 transition-colors"
            >
              {opt.label}
            </button>
          ))}
        </div>
        {error && (
          <p className="mt-3 text-xs text-red-600">{error}</p>
        )}
      </div>
    </div>
  )
}
